/**
 * Куда вернуться после оплаты токенов — и метки этого возврата в адресе.
 *
 * Владелец 02.10: после оплаты человек возвращается на ту страницу, откуда
 * покупал (урок курса, чат специалиста, «Умный поиск»), на своём языке, и
 * токены там уже есть. Окно пакетов шлёт серверу путь страницы (return_path);
 * сервер проверяет его по своему списку (site_server payments.safe_return_path
 * — то же правило, что здесь), зачисляет пакет до переадресации и возвращает
 * на этот путь с ?tokens_paid=1&session_id=…
 *
 * Лёгкий модуль без запросов: его берут ссылки на страницах уроков.
 */

export const TOKENS_PAID_PARAM = "tokens_paid";

const LANGS = ["ru", "en", "cn", "es", "de"];

// Сегмент пути — pchar из RFC 3986, как на сервере (_RETURN_SEGMENT).
const SEGMENT = "[A-Za-z0-9\\-._~%!$&'()*+,;=:@]+";
const RETURN_PATH_RE = new RegExp(
    `^/(?:${LANGS.join("|")})/(?:account(?:/${SEGMENT})*|questions)/?$`,
);
const RETURN_MAX = 500;
// Метки прошлой оплаты и «открыть окно покупки» обратно не несём.
// plan_paid — возврат после оплаты пакета (features/supPlan), как и на сервере.
const DROP_PARAMS = [TOKENS_PAID_PARAM, "plan_paid", "session_id", "payment_success", "inv_id", "buy"];

/**
 * Путь этого сайта, куда вернуть после оплаты, или null: кабинет
 * /{lang}/account/… (там и уроки курсов) и «Умный поиск» /{lang}/questions.
 * Полный адрес, //чужой, «..», обратная косая, пробелы — нет. Строка запроса
 * остаётся без меток оплаты, якорь — нет. Сервер проверяет ещё раз.
 */
export const safeReturnPath = (raw: unknown): string | null => {
    if (typeof raw !== "string") return null;
    const s = raw.trim();
    if (!s || s.length > RETURN_MAX) return null;
    if (!s.startsWith("/") || s.startsWith("//") || s.includes("\\")) return null;
    // eslint-disable-next-line no-control-regex
    if (/[\u0000- \u007f]/.test(s)) return null;
    const hashAt = s.indexOf("#");
    const noHash = hashAt >= 0 ? s.slice(0, hashAt) : s;
    const qAt = noHash.indexOf("?");
    const path = qAt >= 0 ? noHash.slice(0, qAt) : noHash;
    const query = qAt >= 0 ? noHash.slice(qAt + 1) : "";
    if (!RETURN_PATH_RE.test(path)) return null;
    for (const seg of path.split("/")) {
        let plain: string;
        try {
            plain = decodeURIComponent(seg);
        } catch {
            return null;
        }
        if (plain === "." || plain === ".." || plain.includes("/") || plain.includes("\\")) {
            return null;
        }
    }
    const params = new URLSearchParams(query);
    DROP_PARAMS.forEach((k) => params.delete(k));
    const rest = params.toString();
    return rest ? `${path}?${rest}` : path;
};

/** Эта страница — адрес возврата после оплаты (null — не из списка). */
export const currentReturnPath = (): string | null => {
    if (typeof window === "undefined") return null;
    return safeReturnPath(window.location.pathname + window.location.search);
};

export interface TokenReturn {
    /** Сессия Stripe — завершать покупку; null — только убрать метки. */
    sessionId: string | null;
    /** Старый возврат на «Умный поиск» (?payment_success=…). */
    legacy: boolean;
    /** Строка запроса без меток оплаты ("" или "?…"). */
    search: string;
}

// Старый адрес возврата — только «Умный поиск»: на странице курса тот же
// ?payment_success=true&session_id=… значит купленный курс (useCoursePaymentSuccess).
const LEGACY_PATH_RE = /^\/[a-z]{2}\/(?:account\/)?questions\/?$/;

/** Метки возврата со Stripe в адресе этой страницы, или null. */
export const readTokenReturn = (pathname: string, search: string): TokenReturn | null => {
    const params = new URLSearchParams(search);
    const paid = params.get(TOKENS_PAID_PARAM);
    let sessionId: string | null;
    let legacy = false;
    let drop: string[];
    if (paid !== null) {
        sessionId = paid === "1" ? params.get("session_id") || null : null;
        drop = [TOKENS_PAID_PARAM, "session_id"];
    } else if (params.get("payment_success") !== null && LEGACY_PATH_RE.test(pathname)) {
        sessionId = params.get("inv_id") || params.get("session_id") || null;
        legacy = true;
        drop = ["payment_success", "inv_id", "session_id"];
    } else {
        return null;
    }
    drop.forEach((k) => params.delete(k));
    const rest = params.toString();
    return { sessionId, legacy, search: rest ? `?${rest}` : "" };
};
